import { updateBusinessEntity } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToggle,
} from '@commenda/kakashi';
import { AccountingBasis, IBusinessEntity } from '@commenda/types';
import { useEffect, useState } from 'react';
import { useNotification } from '../../../../../hooks/useNotification';

const EditAccountingBasisModal = ({
  isOpen,
  onClose,
  businessEntity,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  businessEntity: IBusinessEntity;
  onSuccess: () => void;
}) => {
  const isUpdatingAccountingBasis = useToggle();

  const [accountingBasis, setAccountingBasis] =
    useState<AccountingBasis>(undefined);

  const { setNotification } = useNotification();

  useEffect(() => {
    if (!businessEntity || !isOpen) return;

    setAccountingBasis(businessEntity.accountingInfo?.accountingBasis);
  }, [businessEntity, isOpen]);

  const close = () => {
    setAccountingBasis(undefined);
    onClose();
  };

  const onSave = async () => {
    if (!accountingBasis) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Accounting basis is required.',
      });
      return;
    }

    isUpdatingAccountingBasis.on();

    const res = await updateBusinessEntity(businessEntity.id, {
      accountingBasis,
    });

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to update accounting basis.',
      });
      isUpdatingAccountingBasis.off();
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Accounting basis updated.',
    });
    setAccountingBasis(undefined);
    onSuccess();
    isUpdatingAccountingBasis.off();
    close();
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent preventCloseOnOutsideClick>
        <DialogHeader>
          <DialogTitle>
            {!!businessEntity.accountingInfo?.accountingBasis
              ? 'Edit Accounting Method'
              : 'Add Accounting Method'}
          </DialogTitle>
        </DialogHeader>
        <div className="p-3xl">
          <FieldWrapper>
            <Label>Accounting Method</Label>
            <Select
              value={accountingBasis}
              onValueChange={(value: AccountingBasis) =>
                setAccountingBasis(value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Accounting Method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AccountingBasis.ACCRUAL}>Accrual</SelectItem>
                <SelectItem value={AccountingBasis.CASH}>Cash</SelectItem>
              </SelectContent>
            </Select>
          </FieldWrapper>
        </div>
        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={close} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onSave}
            disabled={isUpdatingAccountingBasis.visible || !accountingBasis}
          >
            {isUpdatingAccountingBasis.visible ? (
              <LoadingIndicator size={16} />
            ) : null}
            <span>Save</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditAccountingBasisModal;
